import { useState } from 'react';
import type { HomeworkAssignmentSnapshot, StudentProblemSnapshot } from '@wx-weiqi/contracts';

import {
  studentHomeworkResultRows,
  type HomeworkProblemGradingStatus,
  type StudentHomeworkResultRow,
} from '../domain/homework-grading';
import { homeworkBoardFocus } from '../domain/homework-board-focus';
import { formatHomeworkElapsedTime } from '../domain/homework-review-presentation';
import { homeworkListDate, STUDENT_HOMEWORK_STATUSES } from '../domain/homework-list-presentation';
import { BoardSurface } from './BoardSurface';
import { StudentHomeworkBoardDialog } from './StudentHomeworkBoardDialog';
import type { HomeworkStore } from '../stores/homework-store';
import './student-homework-result.css';

const RESULT_LABELS: Readonly<Record<HomeworkProblemGradingStatus, string>> = {
  CORRECT: '正确',
  INCORRECT: '错误',
  PENDING: '待批改',
  UNANSWERED: '未作答',
};

const RESULT_FILTERS = [
  ['ALL', '全部'],
  ['INCORRECT', '错误'],
  ['CORRECT', '正确'],
  ['PENDING', '待批改'],
  ['UNANSWERED', '未作答'],
] as const;

function ResultDate({ label, value }: { readonly label: string; readonly value: string | null }) {
  const formatted = homeworkListDate(value);
  return (
    <span>
      {label}：
      {formatted === null ? (
        '未记录'
      ) : (
        <time dateTime={value!} title="北京时间">
          {formatted.date} {formatted.time}
        </time>
      )}
    </span>
  );
}

export function studentHomeworkOpenAction(
  status: HomeworkAssignmentSnapshot['status'],
): 'OPEN_ANSWER' | 'SHOW_RESULTS' {
  return status === 'REVIEWED' ? 'SHOW_RESULTS' : 'OPEN_ANSWER';
}

function StudentHomeworkQuestionResult({
  row,
  problem,
  onOpen,
}: {
  readonly row: StudentHomeworkResultRow;
  readonly problem: StudentProblemSnapshot | undefined;
  readonly onOpen: () => void;
}) {
  const focus = problem === undefined ? null : homeworkBoardFocus(problem.initialPosition);
  return (
    <article
      className={`student-homework-result__question is-${row.status.toLowerCase()}`}
      aria-label={`第 ${row.questionNumber} 题：${RESULT_LABELS[row.status]}`}
    >
      <button
        type="button"
        className="student-homework-result__open-board"
        disabled={problem === undefined}
        aria-label={`查看第 ${row.questionNumber} 题完整棋盘`}
        onClick={onOpen}
      />
      <div
        className="student-homework-result__preview"
        aria-label={`第 ${row.questionNumber} 题初始局面角部预览`}
      >
        {problem !== undefined && focus !== null ? (
          <BoardSurface
            boardAppearance="wood"
            size={problem.initialPosition.size}
            stones={problem.initialPosition.stones}
            lastMove={null}
            viewport={focus.viewport}
          />
        ) : (
          <span>暂无棋盘预览</span>
        )}
      </div>
      <div className="student-homework-result__question-info">
        <div className="student-homework-result__question-heading">
          <h4>第 {row.questionNumber} 题</h4>
          <span className={`student-homework-result__status is-${row.status.toLowerCase()}`}>
            {RESULT_LABELS[row.status]}
          </span>
        </div>
        <p className="student-homework-result__question-title">{row.title}</p>
        <div className="student-homework-result__question-meta">
          <span>
            {row.elapsedMs === null ? '暂无用时记录' : formatHomeworkElapsedTime(row.elapsedMs)}
          </span>
          <span>{row.attempt === null ? '尚未作答' : `第 ${row.attempt} 次作答`}</span>
        </div>
      </div>
    </article>
  );
}

export function StudentHomeworkResultPanel({
  assignment,
  refreshing,
  homework,
  onBack,
}: {
  readonly assignment: HomeworkAssignmentSnapshot;
  readonly refreshing: boolean;
  readonly homework: Pick<HomeworkStore, 'researchAssignmentProblem'>;
  readonly onBack: () => void;
}) {
  const [filter, setFilter] = useState<(typeof RESULT_FILTERS)[number][0]>('ALL');
  const [openProblemId, setOpenProblemId] = useState<string | null>(null);
  const rows = studentHomeworkResultRows(assignment);
  const problemsById = new Map(
    assignment.items.map((item) => [item.problem.problemId, item.problem] as const),
  );
  const correct = rows.filter((row) => row.status === 'CORRECT').length;
  const incorrect = rows.filter((row) => row.status === 'INCORRECT').length;
  const visibleRows = rows.filter((row) => filter === 'ALL' || row.status === filter);
  const assignmentStatus = STUDENT_HOMEWORK_STATUSES[assignment.status];

  return (
    <section className="student-homework-result" aria-label={`${assignment.title}批改结果`}>
      <header className="student-homework-result__header">
        <button className="return-button" type="button" onClick={onBack}>
          ← 作业列表
        </button>
        <div className="student-homework-result__identity">
          <small>作业批改结果</small>
          <h3>{assignment.title}</h3>
        </div>
        <span className="student-homework-result__assignment-status">{assignmentStatus.label}</span>
      </header>

      <div className="student-homework-result__dates">
        <ResultDate label="布置时间" value={assignment.assignedAt} />
        <ResultDate label="批改时间" value={assignment.reviewedAt} />
      </div>

      <div className="student-homework-result__summary">
        <div>
          <span>题目总数</span>
          <strong>
            {rows.length}
            <small>题</small>
          </strong>
        </div>
        <div className="is-correct">
          <span>正确</span>
          <strong>
            {correct}
            <small>题</small>
          </strong>
        </div>
        <div className="is-incorrect">
          <span>错误</span>
          <strong>
            {incorrect}
            <small>题</small>
          </strong>
        </div>
        <div>
          <span>正确率</span>
          <strong>{rows.length === 0 ? '0.0' : ((correct / rows.length) * 100).toFixed(1)}%</strong>
        </div>
      </div>

      <div className="student-homework-result__list-heading">
        <h4>逐题结果</h4>
        <div className="student-homework-result__filters" role="group" aria-label="按批改结果筛选">
          {RESULT_FILTERS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {label}
              <span>
                {value === 'ALL' ? rows.length : rows.filter((row) => row.status === value).length}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="student-homework-result__body" aria-busy={refreshing}>
        <div className="student-homework-result__questions">
          {visibleRows.map((row) => (
            <StudentHomeworkQuestionResult
              key={row.problemId}
              row={row}
              problem={problemsById.get(row.problemId)}
              onOpen={() => setOpenProblemId(row.problemId)}
            />
          ))}
        </div>
        {visibleRows.length === 0 ? (
          <div className="student-homework-result__empty" role="status">
            <strong>{rows.length === 0 ? '暂无题目结果' : '没有符合条件的题目'}</strong>
            <p>
              {rows.length === 0
                ? '这份作业暂无可展示的题目。'
                : '可以切换其他状态，查看本次作业的批改结果。'}
            </p>
          </div>
        ) : null}
      </div>
      <footer className="student-homework-result__footer">
        <span aria-live="polite">
          {refreshing ? '正在更新…' : `显示 ${visibleRows.length} / ${rows.length} 题`}
        </span>
        <span>棋盘预览为题目初始局面</span>
      </footer>
      {openProblemId !== null && problemsById.has(openProblemId) ? (
        <StudentHomeworkBoardDialog
          key={openProblemId}
          problem={problemsById.get(openProblemId)!}
          questionNumber={rows.find((row) => row.problemId === openProblemId)!.questionNumber}
          onResearch={(answer) =>
            homework.researchAssignmentProblem(assignment.assignmentId, openProblemId, answer)
          }
          onClose={() => setOpenProblemId(null)}
        />
      ) : null}
    </section>
  );
}
